import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite, resolveImg } from "@/context/SiteContext";
import AdminHint from "@/components/admin/AdminHint";

const SLOTS = [
  { key: "home_hero", label: "Home hero background", ratio: "aspect-[16/9]" },
  { key: "logo", label: "Logo", ratio: "aspect-square" },
  { key: "qhht_divider", label: "Forest portal / divider", ratio: "aspect-square" },
  { key: "volunteer_flyer", label: "Volunteer flyer", ratio: "aspect-[3/4]" },
  { key: "home_woman_sea", label: "Home intro photo", ratio: "aspect-[4/3]" },
  { key: "about_isabelle_photo", label: "About Isabelle photo", ratio: "aspect-[3/4]" },
  { key: "about_qhht_hero", label: "About QHHT — hero image", ratio: "aspect-[4/3]" },
  { key: "about_qhht_band", label: "About QHHT — banner image", ratio: "aspect-[16/9]" },
  { key: "what_to_expect_hero", label: "What to Expect — hero image", ratio: "aspect-[4/3]" },
  { key: "what_to_expect_band", label: "What to Expect — banner image", ratio: "aspect-[16/9]" },
  { key: "faq_hero", label: "FAQ — hero image", ratio: "aspect-[4/3]" },
  { key: "book_hero", label: "Book — hero image", ratio: "aspect-[4/3]" },
  { key: "contact_hero", label: "Contact — hero image", ratio: "aspect-[4/3]" },
  { key: "home_card_1", label: "Home — card 1 photo", ratio: "aspect-[3/4]" },
  { key: "home_card_2", label: "Home — card 2 photo", ratio: "aspect-[3/4]" },
  { key: "home_card_3", label: "Home — card 3 photo", ratio: "aspect-[3/4]" },
  { key: "sprig", label: "Lavender sprig accent (PNG)", ratio: "aspect-square" },
];

function SlotCard({ slot, url, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/images/${slot.key}`, fd);
      toast.success(`${slot.label} updated.`);
      await onUploaded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-brand-line p-5">
      <p className="font-body text-sm text-brand-ink font-medium mb-3">{slot.label}</p>
      <div className={`${slot.ratio} w-full overflow-hidden rounded-2xl bg-brand-accent mb-4`}>
        {url ? <img src={url} alt={slot.label} className="h-full w-full object-cover" /> : null}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`upload-input-${slot.key}`} />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`upload-btn-${slot.key}`}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-forest text-white px-4 py-2.5 font-body text-sm hover:bg-brand-forest-dark disabled:opacity-60"
      >
        <UploadCloud size={16} strokeWidth={1.5} /> {busy ? "Uploading…" : "Upload new image"}
      </button>
    </div>
  );
}

export default function ImagesPanel() {
  const { reload } = useSite();
  const [images, setImages] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/images");
    setImages(data);
  };
  useEffect(() => { load(); }, []);

  const onUploaded = async () => {
    await load();
    await reload();
  };

  return (
    <div>
      <AdminHint>
        Every picture on your site is here. Find the one you’d like to change and click <strong>Upload new image</strong>.
        Wide (landscape) photos suit the banners; upright (portrait) photos suit the tall picture frames. Changes go live immediately.
      </AdminHint>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SLOTS.map((s) => (
          <SlotCard key={s.key} slot={s} url={resolveImg(images[s.key])} onUploaded={onUploaded} />
        ))}
      </div>
    </div>
  );
}
